import React, { useCallback, useState } from 'react'
import { isValidPhoneNumber, Country } from 'react-phone-number-input'
import { FieldHookConfig } from 'formik'
import { useField } from '@/hooks/useField'

import ErrorMessage from '@/components/formik/messages/ErrorMessage'
import PhoneNumberInput, { PhoneNumberInputProps } from '@/components/ui/inputs/PhoneNumberInput'
import { useLanguage } from '@/hooks/useLanguage'
import { getCountryCode } from '@/utils/countryCode'

type PhoneNumberFieldProps = FieldHookConfig<string> & Omit<PhoneNumberInputProps, 'onChange' | 'value'>

const MemoizedPhoneNumberInput = React.memo(PhoneNumberInput)

export default function PhoneNumberField(props: PhoneNumberFieldProps) {
  const { name } = props
  const language = useLanguage()
  const [countryCode, setCountryCode] = useState<Country | undefined>(() => getCountryCode(language))
  const [field, meta, helpers] = useField<string>({
    name,
    validate: (value: string) => {
      let errorKey: string | undefined
      if (value && !isValidPhoneNumber(value, countryCode)) {
        errorKey = 'formValidation.phone.invalidNumber'
      }
      return errorKey
    },
  })

  const handleChange = useCallback(
    (value?: string) => {
      helpers.setValue(value || '')
    },
    [helpers]
  )

  const error = !!meta.error && meta.touched && !props.readOnly
  return (
    <MemoizedPhoneNumberInput
      {...props}
      error={error}
      helperText={error && <ErrorMessage error={meta.error} />}
      name={field.name}
      onBlur={field.onBlur}
      onChange={handleChange}
      onCountryChange={setCountryCode}
      value={meta.value}
    />
  )
}
